import React, { useState, useEffect } from 'react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { apiFetch, getApiUrl } from '../utils/api';

function Students({ org }) {
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    start_date: '',
    end_date: '',
    courses: '',
    email: '',
    address: '',
    sin: '',
    year: new Date().getFullYear().toString(),
    amount: '',
  });

  useEffect(() => {
    if (org) {
      fetchStudents();
    }
  }, [org, year]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/organizations/${org.id}/students?year=${year}`, org.tenantId);
      setStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      address: placeData.address || prev.address
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingId ? `/students/${editingId}` : `/organizations/${org.id}/students`;
      const method = editingId ? 'PUT' : 'POST';
      const created = await apiFetch(endpoint, org.tenantId, { method, body: formData });
      if (created) {
        resetForm();
        fetchStudents();
      } else {
        alert('Failed to save student');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving student');
    }
  };

  const handleEdit = (student) => {
    setFormData({
      ...student,
      year: student.year?.toString() || new Date().getFullYear().toString(),
    });
    setEditingId(student.id);
    setShowForm(true);
  };

  const handleDuplicate = (student) => {
    const nextYear = (parseInt(student.year || year) + 1).toString();
    setFormData({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      dob: student.dob || '',
      start_date: '', // Cleared for the new year
      end_date: '',   // Cleared for the new year
      courses: student.courses || '',
      email: student.email || '',
      address: student.address || '',
      sin: student.sin || '',
      year: nextYear,
      amount: '', // Cleared for the new year
    });
    setEditingId(null); // Set to null so it creates a new record
    setYear(nextYear);  // Switch the grid view to the next year automatically
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    try {
      await apiFetch(`/students/${id}`, org.tenantId, { method: 'DELETE' });
      fetchStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
    }
  };

  const handleExportT2202 = () => {
    window.location.href = getApiUrl(`/organizations/${org.id}/t2202/xml?year=${year}&tenantId=${org.tenantId}`);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      dob: '',
      start_date: '',
      end_date: '',
      courses: '',
      email: '',
      address: '',
      sin: '',
      year: year,
      amount: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    import('xlsx').then(XLSX => {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
        
        const rows = jsonData.slice(1);
        const studentsToImport = rows.map(row => {
          let sin = row[9] ? String(row[9]).trim() : '';
          if (!sin) sin = '000-000-000';

          return {
            first_name: row[1] || '',
            last_name: row[2] || '',
            dob: row[3] || '',
            start_date: row[4] || '',
            end_date: row[5] || '',
            courses: row[6] || '',
            email: row[7] || '',
            address: row[8] || '',
            sin: sin,
            amount: parseFloat(row[10]) || 0,
            year: year
          };
        }).filter(s => s.first_name || s.last_name);

        if (studentsToImport.length === 0) {
          alert('No valid student data found in the Excel file.');
          return;
        }

        try {
          const response = await apiFetch(`/organizations/${org.id}/t2202/xml/direct?year=${year}`, org.tenantId, { method: 'POST', body: { students: studentsToImport }, parseResponse: false });

          if (response) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `T2202_Direct_${year}.xml`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            alert('Excel converted to T2202 XML successfully!');
          } else {
            alert('Failed to generate XML from Excel.');
          }
        } catch (error) {
          console.error('Error:', error);
          alert('Error processing file.');
        }

        e.target.value = '';
      };
      reader.readAsArrayBuffer(file);
    }).catch(err => {
      console.error(err);
      alert('Please install the xlsx library by running: npm install xlsx');
    });
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1 text-dark">🎓 Student Database</h2>
          <p className="text-muted mb-0">Manage student records and T2202 forms.</p>
        </div>
        <div className="d-flex gap-2">
          <input 
            type="number" 
            className="form-control" 
            style={{ width: '100px' }} 
            value={year} 
            onChange={(e) => setYear(e.target.value)} 
            title="Academic Year"
          />
          <label className="btn btn-outline-success fw-bold shadow-sm mb-0 d-flex align-items-center">
            <input 
              type="file" 
              accept=".xlsx, .xls, .csv" 
              style={{ display: 'none' }} 
              onChange={handleExcelUpload} 
            />
            📤 Excel to XML
          </label>
          <button className="btn btn-outline-info fw-bold shadow-sm text-dark" onClick={handleExportT2202}>
            📥 Export T2202 (XML)
          </button>
          <button 
            className={`btn fw-bold shadow-sm ${showForm ? 'btn-outline-secondary' : 'btn-primary'}`}
            onClick={() => showForm ? resetForm() : setShowForm(true)}
          >
            {showForm ? 'Cancel' : '➕ Add Student'}
          </button>
        </div>
      </div>

      <div className="card shadow-sm border-0 bg-white mb-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="p-5 text-center text-secondary">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              Loading students...
            </div>
          ) : students.length === 0 ? (
            <div className="p-5 text-center text-muted">
              No student records found for {year}.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-muted small text-uppercase">
                  <tr>
                    <th className="px-4 py-3">S.No</th>
                    <th>Name</th>
                    <th>Last Name</th>
                    <th>D.O.B</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Courses</th>
                    <th>Email</th>
                    <th>Address</th>
                    <th>SIN</th>
                    <th>Amount Paid</th>
                    <th className="text-end px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={student.id}>
                      <td className="px-4">{index + 1}</td>
                      <td className="fw-bold">{student.first_name}</td>
                      <td>{student.last_name}</td>
                      <td>{student.dob}</td>
                      <td>{student.start_date}</td>
                      <td>{student.end_date}</td>
                      <td>{student.courses}</td>
                      <td>{student.email}</td>
                      <td>{student.address}</td>
                      <td>{student.sin}</td>
                      <td className="fw-medium">${student.amount ? parseFloat(student.amount).toFixed(2) : '0.00'}</td>
                      <td className="text-end px-4 text-nowrap">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(student)}>✏️ Edit</button>
                        <button className="btn btn-sm btn-outline-success me-2" onClick={() => handleDuplicate(student)} title="Copy to Next Year">📋 Copy</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(student.id)}>🗑️ Del</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="card shadow-sm border-0 bg-white">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="card-title fw-bold mb-0">{editingId ? 'Edit Student' : 'Add New Student'}</h5>
          </div>
          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">First Name *</label>
                  <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleInputChange} required />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Last Name *</label>
                  <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleInputChange} required />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Year</label>
                  <input type="number" className="form-control" name="year" value={formData.year} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">D.O.B</label>
                  <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleInputChange} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Start Date</label>
                  <input type="date" className="form-control" name="start_date" value={formData.start_date} onChange={handleInputChange} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">End Date</label>
                  <input type="date" className="form-control" name="end_date" value={formData.end_date} onChange={handleInputChange} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-8 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Courses</label>
                  <input type="text" className="form-control" name="courses" value={formData.courses} onChange={handleInputChange} placeholder="e.g. Mathematics 101" />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Amount Paid</label>
                  <input type="number" step="0.01" className="form-control" name="amount" value={formData.amount} onChange={handleInputChange} placeholder="0.00" />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">Email</label>
                  <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label small fw-bold text-muted text-uppercase">SIN *</label>
                  <input type="text" className="form-control" name="sin" value={formData.sin} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small fw-bold text-muted text-uppercase">Address</label>
                <AddressAutocomplete
                  className="form-control"
                  value={formData.address}
                  onChange={handleInputChange}
                  onPlaceSelected={handlePlaceSelected}
                />
              </div>
              <button type="submit" className="btn btn-primary fw-bold px-4 py-2 mt-2">
                {editingId ? 'Update Student' : 'Save Student'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Students;